import {
  Badge,
  Box,
  Divider,
  HStack,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useMemo } from 'react';

import { FormatType, LineChart, type LineData } from '@revelio/replots';

import type { AdjacentRoleWithSalary } from '../hooks/useAdjacentRoleSalaries';
import type { RoleInflowData } from '../hooks/useRoleInflows';
import type { RoleSalaryTrend } from '../hooks/useRoleSalaryTrends';

interface RoleTransitionInsightsPanelProps {
  /** The role selected/expanded on the left panel */
  selectedRole: AdjacentRoleWithSalary | null;
  /** Inflow data for the selected role */
  inflowData: RoleInflowData | undefined;
  isInflowsLoading: boolean;
  /** Salary trends for current + adjacent roles */
  salaryTrends: RoleSalaryTrend[];
  isTrendsLoading: boolean;
  /** Name of the user's current role */
  currentRoleName: string | null;
}

const InflowBar = ({
  name,
  share,
  isCurrentRole,
}: {
  name: string;
  share: number;
  isCurrentRole: boolean;
}) => {
  const displayPercent = (share * 100).toFixed(1);
  const barWidth = Math.max(share * 100 * 3, 4); // Scale up for visibility, min 4%

  return (
    <HStack spacing={2} w="100%">
      <Text
        fontSize="xs"
        w="140px"
        flexShrink={0}
        noOfLines={1}
        fontWeight={isCurrentRole ? 'semibold' : 'normal'}
        color={isCurrentRole ? 'blue.700' : 'gray.700'}
      >
        {name}
        {isCurrentRole && (
          <Text as="span" fontSize="2xs" color="blue.500" ml={1}>
            (you)
          </Text>
        )}
      </Text>
      <Box flex="1" position="relative" h="16px">
        <Box
          h="100%"
          w={`${Math.min(barWidth, 100)}%`}
          bg={isCurrentRole ? 'blue.400' : 'gray.300'}
          borderRadius="sm"
          transition="width 0.3s"
        />
      </Box>
      <Text
        fontSize="xs"
        color="gray.500"
        w="40px"
        textAlign="right"
        flexShrink={0}
      >
        {displayPercent}%
      </Text>
    </HStack>
  );
};

const getFeederStrength = (share: number): string => {
  if (share >= 0.05) {
    return 'your role is a strong feeder';
  }
  if (share >= 0.02) {
    return 'your role is a moderate feeder';
  }
  return 'a less common path';
};

const InflowsSection = ({
  inflowData,
  isLoading,
  selectedRoleName,
  currentRoleName,
}: {
  inflowData: RoleInflowData | undefined;
  isLoading: boolean;
  selectedRoleName: string;
  currentRoleName: string | null;
}) => {
  if (isLoading && !inflowData) {
    return (
      <VStack align="stretch" spacing={2}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.500">
          TYPICAL BACKGROUNDS
        </Text>
        <Text fontSize="2xs" color="gray.400">
          What roles did people hold before becoming a {selectedRoleName}?
        </Text>
        <Skeleton height="20px" borderRadius="sm" />
        <Skeleton height="20px" borderRadius="sm" />
        <Skeleton height="20px" borderRadius="sm" />
      </VStack>
    );
  }

  if (!inflowData || inflowData.inflows.length === 0) {
    return (
      <VStack align="stretch" spacing={2}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.500">
          TYPICAL BACKGROUNDS
        </Text>
        <Text fontSize="xs" color="gray.400">
          No background data available for this role.
        </Text>
      </VStack>
    );
  }

  const { inflows, currentRoleInflowShare } = inflowData;

  return (
    <VStack align="stretch" spacing={2}>
      <Text fontSize="xs" fontWeight="semibold" color="gray.500">
        TYPICAL BACKGROUNDS
      </Text>
      <Text fontSize="2xs" color="gray.400">
        What roles did people hold before becoming a {selectedRoleName}?
      </Text>

      {currentRoleInflowShare !== null && (
        <Box bg="blue.50" px={3} py={1.5} borderRadius="md">
          <Text fontSize="xs" color="blue.700">
            <Text as="span" fontWeight="semibold">
              {(currentRoleInflowShare * 100).toFixed(1)}%
            </Text>{' '}
            of people who became a {selectedRoleName} came from{' '}
            <Text as="span" fontWeight="semibold">
              {currentRoleName ?? 'your role'}
            </Text>{' '}
            — {getFeederStrength(currentRoleInflowShare)}
          </Text>
        </Box>
      )}

      {currentRoleInflowShare === null && (
        <Box bg="orange.50" px={3} py={1.5} borderRadius="md">
          <Text fontSize="xs" color="orange.700">
            {currentRoleName ?? 'Your role'} doesn't commonly appear as a prior
            role — this may be a less direct transition.
          </Text>
        </Box>
      )}

      <VStack align="stretch" spacing={1}>
        {inflows.map((inflow) => (
          <InflowBar
            key={inflow.id}
            name={inflow.longName || inflow.shortName}
            share={inflow.inflowShare}
            isCurrentRole={inflow.isCurrentRole}
          />
        ))}
      </VStack>
    </VStack>
  );
};

/** Colors for salary trend lines */
const CURRENT_ROLE_COLOR = '#3182CE'; // blue.500
const SELECTED_ROLE_COLOR = '#805AD5'; // purple.500
const OTHER_ROLE_COLORS = ['#A0AEC0', '#CBD5E0']; // gray.400, gray.300

interface TrendWithGrowth extends RoleSalaryTrend {
  growthPercent: number;
  color: string;
}

const getLineColor = (
  trend: RoleSalaryTrend,
  selectedRoleId: number | null,
  otherColorIdx: number
): string => {
  if (trend.isCurrentRole) {
    return CURRENT_ROLE_COLOR;
  }
  if (selectedRoleId !== null && trend.roleId === selectedRoleId) {
    return SELECTED_ROLE_COLOR;
  }
  return (
    OTHER_ROLE_COLORS[otherColorIdx % OTHER_ROLE_COLORS.length] ?? '#A0AEC0'
  );
};

const SalaryTrendsSection = ({
  trends,
  isLoading,
  selectedRoleId,
}: {
  trends: RoleSalaryTrend[];
  isLoading: boolean;
  selectedRoleId: number | null;
}) => {
  const { chartData, orderedTrends } = useMemo((): {
    chartData: LineData[];
    orderedTrends: TrendWithGrowth[];
  } => {
    if (trends.length === 0) {
      return { chartData: [], orderedTrends: [] };
    }

    // Order: current role first, selected role second, then others
    const currentTrend = trends.find((t) => t.isCurrentRole);
    const selectedTrend = selectedRoleId
      ? trends.find((t) => t.roleId === selectedRoleId && !t.isCurrentRole)
      : null;
    const otherTrends = trends.filter(
      (t) =>
        !t.isCurrentRole &&
        (selectedRoleId ? t.roleId !== selectedRoleId : true)
    );

    const ordered = [currentTrend, selectedTrend, ...otherTrends].filter(
      (t): t is RoleSalaryTrend => t !== null && t !== undefined
    );

    let otherColorIdx = 0;
    const trendsWithMeta: TrendWithGrowth[] = [];
    const lines: LineData[] = [];

    for (const trend of ordered) {
      const color = getLineColor(trend, selectedRoleId, otherColorIdx);
      if (
        !trend.isCurrentRole &&
        !(selectedRoleId !== null && trend.roleId === selectedRoleId)
      ) {
        otherColorIdx += 1;
      }

      const isHighlighted =
        trend.isCurrentRole ||
        (selectedRoleId !== null && trend.roleId === selectedRoleId);

      const label = trend.isCurrentRole
        ? `${trend.roleName} (current)`
        : trend.roleName;

      const first = trend.trend[0]?.salary ?? 0;
      const last = trend.trend[trend.trend.length - 1]?.salary ?? 0;
      const growthPercent = first > 0 ? ((last - first) / first) * 100 : 0;

      trendsWithMeta.push({ ...trend, growthPercent, color });

      lines.push({
        label,
        color,
        strokeWidth: isHighlighted ? 2.5 : 1.5,
        values: trend.trend.map((point) => ({
          date: point.month,
          value: point.salary,
        })),
      });
    }

    return { chartData: lines, orderedTrends: trendsWithMeta };
  }, [trends, selectedRoleId]);

  if (isLoading) {
    return (
      <VStack align="stretch" spacing={2}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.500">
          MEDIAN SALARY OVER TIME
        </Text>
        <Skeleton height="180px" borderRadius="md" />
      </VStack>
    );
  }

  if (chartData.length === 0) {
    return null;
  }

  return (
    <VStack align="stretch" spacing={2}>
      <Text fontSize="xs" fontWeight="semibold" color="gray.500">
        MEDIAN SALARY OVER TIME
      </Text>
      <Text fontSize="2xs" color="gray.400">
        How has median pay trended for your role vs potential moves?
      </Text>

      <Box h="200px" w="100%">
        <LineChart
          data={chartData}
          format={FormatType.CURRENCY}
          dateFormat="month"
          showLegend
          showGridLines
          curveTension={0.3}
          fontSize={10}
        />
      </Box>

      {/* Growth summary per role */}
      <VStack align="stretch" spacing={1}>
        {orderedTrends.map((trend) => (
          <HStack
            key={trend.roleId}
            spacing={2}
            px={2}
            py={1}
            borderRadius="sm"
          >
            <Box
              w="10px"
              h="10px"
              borderRadius="full"
              bg={trend.color}
              flexShrink={0}
            />
            <Text fontSize="xs" flex="1" noOfLines={1} color="gray.700">
              {trend.roleName}
              {trend.isCurrentRole && (
                <Text as="span" color="blue.500" ml={1}>
                  (current)
                </Text>
              )}
            </Text>
            <Badge
              colorScheme={trend.growthPercent >= 0 ? 'green' : 'red'}
              fontSize="2xs"
              variant="subtle"
              flexShrink={0}
            >
              {trend.growthPercent >= 0 ? '+' : ''}
              {trend.growthPercent.toFixed(1)}% over {trend.trend.length}mo
            </Badge>
          </HStack>
        ))}
      </VStack>
    </VStack>
  );
};

export const RoleTransitionInsightsPanel = ({
  selectedRole,
  inflowData,
  isInflowsLoading,
  salaryTrends,
  isTrendsLoading,
  currentRoleName,
}: RoleTransitionInsightsPanelProps) => {
  if (!selectedRole) {
    return (
      <VStack align="stretch" spacing={3}>
        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
          Role Transition Insights
        </Text>
        <Box bg="gray.50" p={4} borderRadius="md" textAlign="center">
          <Text color="gray.500" fontSize="sm">
            Select a role on the left for detailed insights
          </Text>
          <Text color="gray.400" fontSize="xs" mt={1}>
            Typical backgrounds, salary trends, and more
          </Text>
        </Box>

        {/* Still show salary trends even without selection */}
        <SalaryTrendsSection
          trends={salaryTrends}
          isLoading={isTrendsLoading}
          selectedRoleId={null}
        />
      </VStack>
    );
  }

  const selectedRoleName = selectedRole.longName || selectedRole.shortName;

  return (
    <VStack align="stretch" spacing={3}>
      <HStack spacing={2}>
        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
          Role Transition Insights
        </Text>
        <Badge colorScheme="purple" fontSize="2xs" variant="subtle">
          {selectedRoleName}
        </Badge>
      </HStack>

      <InflowsSection
        inflowData={inflowData}
        isLoading={isInflowsLoading}
        selectedRoleName={selectedRoleName}
        currentRoleName={currentRoleName}
      />

      <Divider />

      <SalaryTrendsSection
        trends={salaryTrends}
        isLoading={isTrendsLoading}
        selectedRoleId={selectedRole.id}
      />
    </VStack>
  );
};
